import { Component, computed, DestroyRef, effect, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    FormArray,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ImportsRunnerListingToMappedIdMapping, ImportsRunnerPayload } from 'exports/interfaces/imports-runner.interface';
import { ImportsRunnerService } from 'exports/services/imports-runner.service';
import { Listing } from 'listings/interfaces/listing.interface';
import { ListingsGridService } from 'listings/services/listings-grid.service';
import { filter, map, startWith, switchMap } from 'rxjs';
import { Supplier } from 'settings/interfaces/supplier.interface';
import { ChannelsService } from 'settings/services/channels.service';
import { SuppliersService } from 'settings/services/suppliers.service';
import { DataGridComponent } from 'shared/components/data-grid/data-grid.component';
import { DefaultDataGridActionCellRendererComponent } from 'shared/components/data-grid/default-data-grid-action-cell-renderer/default-data-grid-action-cell-renderer.component';
import { FormErrorComponent } from 'shared/components/form-error/form-error.component';
import { TypeaheadSelectComponent } from 'shared/components/typeahead-select/typeahead-select.component';
import { TooltipDirective } from 'shared/directives/tooltip.directive';
import { DataGridColumnDefinition } from 'shared/interfaces/data-grid.interface';
import { ModalService, ToastService } from 'shared/services';
import { channelMappedIdValidator } from 'shared/validators/channel-mapped-id-validator';
import { ImportsRunnerBulkAddModalComponent } from '../imports-runner-bulk-add-modal/imports-runner-bulk-add-modal.component';
import { ImportsRunnerConfirmationModalComponent } from '../imports-runner-confirmation-modal/imports-runner-confirmation-modal.component';

interface ListingToMappedIdMappingControls {
    listingId: FormControl<string>;
    channelMappedId: FormControl<string>;
}

@Component({
    selector: 'app-imports-runner',
    imports: [TranslateModule, ReactiveFormsModule, FormErrorComponent, TypeaheadSelectComponent, DataGridComponent, TooltipDirective],
    templateUrl: './imports-runner.component.html',
    styleUrl: './imports-runner.component.scss',
})
export class ImportsRunnerComponent {
    form = new FormGroup<{
        channelId: FormControl<string>;
        supplierId: FormControl<string>;
        brandId: FormControl<string>;
        importItems: FormArray<FormGroup<ListingToMappedIdMappingControls>>;
    }>({
        channelId: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        supplierId: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        brandId: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        importItems: new FormArray<FormGroup<ListingToMappedIdMappingControls>>([], { validators: [Validators.required] }),
    });

    supplierIdValue = toSignal(this.form.get('supplierId')!.valueChanges, { initialValue: this.form.get('supplierId')!.value });
    channelIdValue = toSignal(this.form.get('channelId')!.valueChanges, { initialValue: this.form.get('channelId')!.value });
    brandIdValue = toSignal(this.form.get('brandId')!.valueChanges, { initialValue: this.form.get('brandId')!.value });
    importItemsValue = toSignal(this.form.get('importItems')!.valueChanges, { initialValue: this.form.get('importItems')!.value });
    importItemsData = computed(() => {
        return this.importItemsValue().map((item) => {
            const returnVal = {
                channelMappedId: item.channelMappedId,
            };

            if (!item.listingId) {
                return {
                    listing: null,
                    ...returnVal,
                };
            }

            return {
                listing: this.supplierListings().find((listing: any) => listing.id === item.listingId),
                ...returnVal,
            };
        });
    });
    formValid = toSignal(
        this.form.statusChanges.pipe(
            map((status) => status === 'VALID'),
            startWith(this.form.valid),
        ),
        { initialValue: this.form.valid },
    );

    public addImportItemForm = new FormGroup<{
        listingId: FormControl<string>;
        channelMappedId: FormControl<string>;
    }>(
        {
            listingId: new FormControl<string>('', { nonNullable: true }),
            channelMappedId: new FormControl<string>('', {
                nonNullable: true,
                validators: [Validators.required, channelMappedIdValidator()],
            }),
        },
        {
            validators: [
                this.listingOrChannelIdNotAlreadyInList(
                    () => this.form.controls.importItems.value as ImportsRunnerListingToMappedIdMapping[],
                ),
            ],
        },
    );

    public supplierListings = signal<Listing[]>([]);
    public supplierListingOptions = computed(() => {
        return this.supplierListings().filter(
            (listing) => !this.importItemsValue().find((addedListing) => addedListing.listingId === listing.id),
        );
    });
    public isImportItemAddAllowed = computed(() => {
        return !!this.supplierIdValue() && !!this.channelIdValue() && !!this.brandIdValue();
    });

    importItemColumns: DataGridColumnDefinition[] = this.generateImportItemsTableColumns();

    constructor(
        public channelsService: ChannelsService,
        public suppliersService: SuppliersService,
        public listingsService: ListingsGridService,
        private importsRunnerService: ImportsRunnerService,
        private toastService: ToastService,
        private destroyRef: DestroyRef,
        private modalService: ModalService,
    ) {
        this.channelsService.loadImportsEligibleChannels.set(true);
        this.suppliersService.loadAllSuppliers.set(true);

        // Enable/Disable import item form based on form completion
        effect(() => {
            if (this.isImportItemAddAllowed()) {
                this.addImportItemForm.enable();
            } else {
                this.addImportItemForm.disable();
                this.addImportItemForm.reset();
                this.form.controls.importItems.reset();
            }
        });

        // Remove all import items when channel changes
        effect(() => {
            // We don't care about the value, we just want to react to it being changed
            this.channelIdValue();

            for (let i = this.importItems.length - 1; i >= 0; i--) {
                this.importItems.removeAt(i);
            }
        });

        // Remove import items with an assigned listing when brand changes
        effect(() => {
            if (this.brandIdValue()) {
                const indicesToRemove: number[] = [];

                this.importItems.controls.forEach((control, index) => {
                    if (control.value.listingId) {
                        indicesToRemove.push(index);
                    }
                });

                for (let i = indicesToRemove.length - 1; i >= 0; i--) {
                    this.importItems.removeAt(indicesToRemove[i]);
                }
            }
        });

        // Update brand codes and listings selection on supplier ID change
        effect(() => {
            const supplierId = this.supplierIdValue();
            this.suppliersService.supplierIdForBrandCodes.set(supplierId || null);

            if (!supplierId || !this.brandIdValue()) {
                return;
            }

            const currentSupplier = this.suppliersService.allSuppliersList().find((supplier: Supplier) => supplier.id === supplierId);
            this.importsRunnerService
                .getSupplierListingsForSyncImport(currentSupplier.companyName, this.brandIdValue())
                .subscribe((listingsResult) => {
                    this.supplierListings.set(listingsResult);
                });
        });

        // Disable brand control if no brands exist
        effect(() => {
            const brandsBySupplier = this.suppliersService.brandListBySupplier();
            const brandIdControl = this.form.get('brandId')!;

            if (brandsBySupplier.length === 0) {
                brandIdControl.setValue('');
                brandIdControl.disable();
            } else {
                brandIdControl.setValue(brandsBySupplier[0].value);
                brandIdControl.enable();
            }
        });
    }

    get importItems(): FormArray<FormGroup<ListingToMappedIdMappingControls>> {
        return this.form.get('importItems') as FormArray<FormGroup<ListingToMappedIdMappingControls>>;
    }

    addNewListingMapping() {
        this.addImportItemForm.markAllAsTouched();
        this.addImportItemForm.updateValueAndValidity();

        if (this.addImportItemForm.invalid) {
            return;
        }

        const newItem: ImportsRunnerListingToMappedIdMapping =
            this.addImportItemForm.getRawValue() as ImportsRunnerListingToMappedIdMapping;
        this.insertMapping(newItem);

        this.addImportItemForm.reset();
    }

    removeListingMapping(data: any): void {
        const index = this.importItems.controls.findIndex((control) => control.value.channelMappedId === data.channelMappedId);
        this.importItems.removeAt(index);
    }

    openBulkAddModal() {
        this.modalService
            .openModal(ImportsRunnerBulkAddModalComponent, {
                type: 'center',
            })
            .onClose.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean))
            .subscribe((data) => {
                if (data && data.length) {
                    const totalItems = data.length;
                    let addedItems = 0;
                    data.forEach((mappedId: string) => {
                        if (!this.importItems.value.find((item: any) => item.channelMappedId === mappedId)) {
                            const newItem: ImportsRunnerListingToMappedIdMapping = {
                                channelMappedId: mappedId,
                                listingId: '',
                            };

                            this.insertMapping(newItem);
                            addedItems++;
                        }
                    });

                    if (totalItems === addedItems) {
                        this.toastService.showToast('exports.imports-runner.bulkAddIds.toast.success', 'success', {
                            addedCount: addedItems,
                        });
                    } else {
                        this.toastService.showToast(
                            'exports.imports-runner.bulkAddIds.toast.successWithWarnings',
                            'warning',
                            {
                                addedCount: addedItems,
                                ignoredCount: totalItems - addedItems,
                            },
                            10000,
                        );
                    }
                }
            });
    }

    runImport() {
        this.form.updateValueAndValidity();
        this.form.markAllAsTouched();

        if (this.form.invalid) {
            return;
        }

        const formValue = this.form.value;
        if (!formValue.channelId || !formValue.supplierId || !formValue.brandId || !formValue.importItems) {
            return;
        }

        this.modalService
            .openModal(ImportsRunnerConfirmationModalComponent, {
                type: 'center',
                data: {
                    ...this.form.value,
                    channelName: this.channelsService
                        .importsEligibleChannels()
                        .find((channel) => channel.value === this.form.value.channelId)?.label,
                    supplierName: this.suppliersService.allSuppliersList().find((supp: any) => supp.id == this.form.value.supplierId)
                        ?.companyName,
                    brandName: this.suppliersService.brandListBySupplier().find((brand) => brand.value == this.form.value.brandId)?.label,
                    importItems: this.importItemsData(),
                },
            })
            .onClose.pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(Boolean),
                switchMap(() => {
                    const payload: ImportsRunnerPayload = {
                        contentHubChannelId: formValue.channelId!,
                        supplierId: formValue.supplierId!,
                        brandId: formValue.brandId!,
                        importItems: formValue.importItems as ImportsRunnerListingToMappedIdMapping[],
                    };

                    return this.importsRunnerService.startBulkImport(payload).pipe(takeUntilDestroyed(this.destroyRef));
                }),
            )
            .subscribe(() => {
                this.toastService.showToast('exports.imports-runner.successStart', 'success');
                this.form.reset();
                this.importItems.clear();
            });
    }

    private listingOrChannelIdNotAlreadyInList(getImportGroup: () => ImportsRunnerListingToMappedIdMapping[]): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const mappingForm = group as FormGroup<ListingToMappedIdMappingControls>;
            const listingId = mappingForm.controls.listingId?.value;
            const mappedId = mappingForm.controls.channelMappedId?.value;

            if (!listingId && !mappedId) return null;

            const listingDuplicate = (getImportGroup() ?? []).some((x) => listingId && x.listingId === listingId);
            const mappedIdDuplicate = (getImportGroup() ?? []).some((x) => x.channelMappedId.trim() === mappedId.trim());

            if (listingDuplicate) {
                return { listingAlreadyInImportList: true };
            }

            if (mappedIdDuplicate) {
                return { mappedIdAlreadyInImportList: true };
            }

            return null;
        };
    }

    private createItemGroup(item: ImportsRunnerListingToMappedIdMapping): FormGroup<ListingToMappedIdMappingControls> {
        return new FormGroup<ListingToMappedIdMappingControls>({
            listingId: new FormControl<string>(item.listingId, { nonNullable: true }),
            channelMappedId: new FormControl<string>(item.channelMappedId.trim(), { nonNullable: true }),
        });
    }

    private insertMapping(newItem: ImportsRunnerListingToMappedIdMapping) {
        if (newItem.listingId) {
            const firstItemWithoutListingIndex = this.importItems.controls.findIndex((item) => !item.value.listingId);
            this.importItems.insert(firstItemWithoutListingIndex, this.createItemGroup(newItem));
        } else {
            this.importItems.push(this.createItemGroup(newItem));
        }
    }

    private generateImportItemsTableColumns(): DataGridColumnDefinition[] {
        return [
            {
                headerLabel: 'exports.imports-runner.labels.channelMappedId',
                dataKey: 'channelMappedId',
            },
            {
                headerLabel: 'listings.table.shijiId',
                dataKey: 'listing.shijiId',
                customCellRendererData: {
                    defaultValueIfNull: 'exports.imports-runner.importItems.createNewListingPlaceholder',
                },
            },
            {
                headerLabel: 'listings.general.listingName.label',
                dataKey: 'listing.name',
            },
            {
                headerLabel: '',
                customCellRenderer: DefaultDataGridActionCellRendererComponent,
                customCellRendererData: [
                    {
                        label: 'generalKeywords.remove',
                        icon: 'trash',
                        clickHandler: this.removeListingMapping.bind(this),
                    },
                ],
            },
        ];
    }
}
